import { CreateStoryDialog } from "@/dialogs/create-story-dialog";
import { track } from "@/features/analytics";
import { FUNNEL_PATHS } from "@/pages/how-to-start/funnel-paths";
import { usePlatformFunnelStore } from "@/pages/how-to-start/platform-funnel.store";
import { useAuthStoreShallow } from "@/features/authentication";
import { useStandaloneVersions } from "@/features/standalone-versions";
import { FEATURE_FLAGS, useFeatureFlag } from "@/lib/feature-flags";
import Tooltip from "@components/ui/tooltip";
import useAddSlides from "@editor/hooks/use-add-slides";
import { cn } from "@lib/cn";
import {
  SlideDeleteManyDialog,
  SlideMoveManyDialog,
} from "@modules/slides-library/features/saved-slides";
import {
  getRecentlyUsedCategory,
  useRecentlyUsedStore,
  useSlidesLibraryStore,
  useSlidesLibraryStoreShallow,
  type SelectedSlide,
  type SlidesLibraryState,
} from "@modules/slides-library/shared";
import { useAppDispatch } from "@store";
import { closeDialog, openDialog } from "@store/dialogs";
import {
  IconFolderSymlink,
  IconMinus,
  IconPlus,
  IconSparkles,
  IconTrash,
} from "@tabler/icons-react";
import React, { useCallback } from "react";
import styles from "../styles/dialog.module.scss";
import {
  canCreateFromSlides,
  canCreateWithAi,
  effectiveCreateTarget,
} from "../utils/create-from-slides";
import { slidesTemplateStory } from "../utils/slide-selection";

type ActionButtonProps = {
  multiSelectSlides: SlidesLibraryState["multiSelectSlides"];
};

const recordBatchRecentUse = (selected: SelectedSlide[]) => {
  const { type, templateTab } = useSlidesLibraryStore.getState();
  const category = getRecentlyUsedCategory(type, templateTab);
  if (!category) return;
  const { recordUse, recordSavedSlide } = useRecentlyUsedStore.getState();
  // Each recordUse/recordSavedSlide dedupes and prepends, so after the loop
  // the FIRST iterated id ends up at the head of the MRU list.
  for (const item of selected) {
    if (category === "user-slides" || category === "team-slides") {
      if (item.savedSlide) recordSavedSlide(category, item.savedSlide);
    } else {
      recordUse(category, item.id);
    }
  }
};

export const SlidesLibraryFloatingActions = React.memo(
  ({ dialogId }: { dialogId: string }) => {
    const {
      multiSelectSlides,
      resetMultiSelectSlides,
      hasUnsavedSlides,
      view,
      onSuccess,
      onPickSlides,
      isImportFromStories,
      type,
      createTarget,
    } = useSlidesLibraryStoreShallow((s) => ({
      multiSelectSlides: s.multiSelectSlides,
      resetMultiSelectSlides: s.resetMultiSelectSlides,
      hasUnsavedSlides: s.hasUnsavedSlides,
      view: s.view,
      onSuccess: s.onSuccessCallback,
      onPickSlides: s.onPickSlidesCallback,
      isImportFromStories:
        s.templateTab === "import-from-stories" &&
        !!s.importStoriesSelectedStoryId,
      type: s.type,
      createTarget: s.createTarget,
    }));
    const { isUser, isCreatorLite, isUserOrCreatorLite } = useAuthStoreShallow(
      (s) => ({
        isUser: s.isUser,
        isCreatorLite: s.isCreatorLite,
        isUserOrCreatorLite: s.isUserOrCreatorLite,
      }),
    );
    // "Create with AI" is AI on slides, rolled out per org behind its flag.
    const aiSlidesOn = useFeatureFlag(FEATURE_FLAGS.AI_SLIDES);
    const versionsOn = useStandaloneVersions();
    const cannotManageTeamSlides =
      type === "team-slides" && isUserOrCreatorLite;
    // What these slides would become, and who may make it -- see
    // ../../utils/create-from-slides. A Creator lite opening the library from
    // their own sidebar has said nothing about what they want, and a version
    // is the only thing they can have, so that is what they are offered.
    const where = {
      isUser,
      isCreatorLite,
      versionsOn,
      view,
      createTarget: effectiveCreateTarget({
        isCreatorLite,
        versionsOn,
        view,
        createTarget,
      }),
    };

    if (multiSelectSlides.size === 0) return null;

    return (
      <div
        className={cn(
          "tw-absolute tw-bottom-2 tw-left-1/2 tw-z-[9999] tw-flex tw-h-11 -tw-translate-x-1/2 tw-items-center tw-gap-10 tw-rounded-xl tw-bg-white tw-py-1 tw-pl-4 tw-pr-1",
          styles["floating-shadow"],
        )}
      >
        <SelectedSlidesCount
          multiSelectSlides={multiSelectSlides}
          resetMultiSelectSlides={resetMultiSelectSlides}
        />
        <div className="tw-flex tw-items-center tw-gap-0.5">
          {/* A PICKER HAS ONE ANSWER. Opened to choose slides for somewhere
              else -- the funnel's outline -- the bar offers only that. */}
          {onPickSlides ? (
            <PickSlidesAction
              multiSelectSlides={multiSelectSlides}
              onPickSlides={onPickSlides}
              dialogId={dialogId}
            />
          ) : (
            <>
              {!isImportFromStories &&
                !hasUnsavedSlides &&
                !(view === "library" && isUserOrCreatorLite) &&
                !cannotManageTeamSlides && (
                  <>
                    <MoveToFolder multiSelectSlides={multiSelectSlides} />
                    <RemoveFromSaved multiSelectSlides={multiSelectSlides} />
                  </>
                )}
              {/* A Creator lite may not create a story, but a standalone
                  version is theirs to make. */}
              <CreateStoryAction
                view={view}
                multiSelectSlides={multiSelectSlides}
                onSuccess={onSuccess}
                createTarget={where.createTarget}
                disabled={!canCreateFromSlides(where)}
                dialogId={dialogId}
              />
              {/* A version is a Creator lite's to make, AI or not, so the
                  picked slides may be written with AI when that is what the
                  library was opened for. A story is still not theirs to
                  create (see pages/how-to-start/components/how-to-start), and
                  a viewer creates nothing at all. */}
              {aiSlidesOn && canCreateWithAi(where) && (
                <CreateWithAiAction
                  multiSelectSlides={multiSelectSlides}
                  createTarget={where.createTarget}
                  dialogId={dialogId}
                />
              )}
            </>
          )}
        </div>
      </div>
    );
  },
);
SlidesLibraryFloatingActions.displayName = "SlidesLibraryFloatingActions";

const SelectedSlidesCount = React.memo<{
  multiSelectSlides: SlidesLibraryState["multiSelectSlides"];
  resetMultiSelectSlides: () => void;
}>(({ multiSelectSlides, resetMultiSelectSlides }) => {
  const handleReset = useCallback(() => {
    resetMultiSelectSlides();
  }, [resetMultiSelectSlides]);

  return (
    <div className="tw-flex tw-items-center tw-gap-3">
      <button
        type="button"
        onClick={handleReset}
        className="tw-flex tw-size-4 tw-shrink-0 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded tw-border-none tw-bg-primary tw-p-0 tw-outline-none"
      >
        <IconMinus className="tw-size-3 tw-text-white" stroke={2} />
      </button>
      <span className="tw-whitespace-nowrap tw-font-sans tw-text-[14px] tw-font-medium tw-text-blue">
        {multiSelectSlides.size} slides selected
      </span>
    </div>
  );
});

/**
 * "Add N slides", when the library was opened to pick slides for somewhere
 * else. Hands the picks over in the order they were made and closes.
 */
const PickSlidesAction = React.memo<{
  multiSelectSlides: SlidesLibraryState["multiSelectSlides"];
  onPickSlides: (slides: SelectedSlide[]) => void;
  dialogId: string;
}>(({ multiSelectSlides, onPickSlides, dialogId }) => {
  const dispatch = useAppDispatch();
  const count = multiSelectSlides.size;
  const handleAction = useCallback(() => {
    track("add slides to outline", { from: "slides toolbar", count });
    onPickSlides(Array.from(multiSelectSlides.values()));
    dispatch(closeDialog(dialogId));
  }, [dispatch, multiSelectSlides, onPickSlides, dialogId, count]);

  return (
    <button
      type="button"
      onClick={handleAction}
      className="tw-flex tw-h-9 tw-cursor-pointer tw-items-center tw-justify-center tw-gap-1.5 tw-whitespace-nowrap tw-rounded-lg tw-border-none tw-bg-primary tw-px-3.5 tw-font-sans tw-text-[13px] tw-font-medium tw-text-white tw-shadow-2xs hover:tw-bg-violet-500"
    >
      <IconPlus className="tw-size-4 tw-shrink-0 !tw-text-white" stroke={1.5} />
      {count === 1 ? "Add slide" : `Add ${count} slides`}
    </button>
  );
});

const MoveToFolder = React.memo<ActionButtonProps>(({ multiSelectSlides }) => {
  const dispatch = useAppDispatch();
  const handleAction = useCallback(() => {
    dispatch(
      openDialog({
        id: "move-many-slides-dialog",
        content: SlideMoveManyDialog,
        props: { ids: Array.from(multiSelectSlides.keys()) },
      }),
    );
  }, [dispatch, multiSelectSlides]);

  return (
    <button
      type="button"
      onClick={handleAction}
      className="tw-bg-transparent tw-flex tw-h-9 tw-w-9 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-lg tw-border-none tw-p-0 tw-outline-none hover:tw-bg-blue-50"
    >
      <IconFolderSymlink className="tw-size-4 tw-text-blue" stroke={1} />
    </button>
  );
});

const RemoveFromSaved = React.memo<ActionButtonProps>(
  ({ multiSelectSlides }) => {
    const dispatch = useAppDispatch();
    const handleAction = useCallback(() => {
      dispatch(
        openDialog({
          id: "delete-many-slides-dialog",
          content: SlideDeleteManyDialog,
          props: { ids: Array.from(multiSelectSlides.keys()) },
        }),
      );
    }, [dispatch, multiSelectSlides]);

    return (
      <button
        type="button"
        onClick={handleAction}
        className="tw-bg-transparent tw-flex tw-h-9 tw-w-9 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-lg tw-border-none tw-p-0 tw-outline-none hover:tw-bg-blue-50"
      >
        <IconTrash className="tw-size-4 tw-text-blue" stroke={1} />
      </button>
    );
  },
);

const CreateStoryAction = React.memo<{
  view: "library" | "editor";
  multiSelectSlides: SlidesLibraryState["multiSelectSlides"];
  onSuccess: SlidesLibraryState["onSuccessCallback"];
  createTarget: SlidesLibraryState["createTarget"];
  disabled?: boolean;
  dialogId: string;
}>(
  ({
    view,
    multiSelectSlides,
    onSuccess,
    createTarget,
    disabled,
    dialogId,
  }) => {
    const dispatch = useAppDispatch();
    const { handleAddSlides } = useAddSlides();
    const handleAction = useCallback(() => {
      if (disabled) return;
      if (view === "editor") {
        track("add slides", {
          from: "slides toolbar",
          count: multiSelectSlides.size,
        });
        recordBatchRecentUse(Array.from(multiSelectSlides.values()));
        handleAddSlides(
          Array.from(multiSelectSlides.values()).map((slide) => slide.slide),
        );
      }
      if (view === "library") {
        track(createTarget === "version" ? "create version" : "create story", {
          from: "slides toolbar",
          count: multiSelectSlides.size,
        });
        dispatch(
          openDialog({
            id: CreateStoryDialog.dialogId,
            content: CreateStoryDialog,
            props: {
              templateStory: slidesTemplateStory(multiSelectSlides.values()),
              // A version has no folder of its own.
              withFolder: createTarget !== "version",
              forceUseTemplateSlides: true,
              createTarget,
              onSuccess,
            },
          }),
        );
      }
      dispatch(closeDialog(dialogId));
    }, [
      dispatch,
      view,
      multiSelectSlides,
      handleAddSlides,
      disabled,
      onSuccess,
      createTarget,
      dialogId,
    ]);

    const button = (
      <button
        type="button"
        onClick={handleAction}
        className={cn(
          "tw-flex tw-h-9 tw-cursor-pointer tw-items-center tw-justify-center tw-gap-1.5 tw-whitespace-nowrap tw-rounded-lg tw-bg-white tw-px-3 tw-font-sans tw-text-[13px] tw-font-medium tw-text-blue tw-shadow-xs tw-outline tw-outline-1 !tw-outline-offset-0 tw-outline-blue-100 hover:tw-bg-blue-50",
          disabled && "tw-cursor-not-allowed tw-opacity-50",
        )}
        disabled={disabled}
      >
        <IconPlus className="tw-size-4 tw-shrink-0 tw-text-blue" stroke={1.5} />
        {view === "library"
          ? createTarget === "version"
            ? "Create version"
            : "Use slides"
          : "Add slides"}
      </button>
    );

    if (disabled) {
      return (
        <Tooltip placement="top">
          <Tooltip.Trigger asChild>
            <span>{button}</span>
          </Tooltip.Trigger>
          <Tooltip.Content className="tw-z-[99999]">
            <Tooltip.Text>
              Your role doesn't allow creating new stories
            </Tooltip.Text>
          </Tooltip.Content>
        </Tooltip>
      );
    }

    return button;
  },
);

/**
 * "Create with AI" on a set of picked slides (Figma 991:119120): the slides
 * become the template of a new document, in the order they were picked, and
 * the funnel opens on its prompt step -- the outline is written for these
 * slides, then each is filled with the new content in the open editor. The
 * same path a template or one of the user's own documents takes; the slides
 * simply arrive as a story assembled here rather than one stored anywhere.
 */
const CreateWithAiAction = React.memo<{
  multiSelectSlides: SlidesLibraryState["multiSelectSlides"];
  createTarget: SlidesLibraryState["createTarget"];
  dialogId: string;
}>(({ multiSelectSlides, createTarget, dialogId }) => {
  const dispatch = useAppDispatch();
  const openFunnel = usePlatformFunnelStore((s) => s.openFunnel);
  const handleAction = useCallback(() => {
    track(
      createTarget === "version"
        ? "create version with ai"
        : "create story with ai",
      { from: "slides toolbar", count: multiSelectSlides.size },
    );
    const templateStory = slidesTemplateStory(multiSelectSlides.values());
    dispatch(closeDialog(dialogId));
    // The deck the funnel writes becomes what the library was opened to
    // make: a story of the user's own, or a version.
    openFunnel(
      FUNNEL_PATHS.aiDescribe,
      {
        docType: "document",
        templateSource: "slides",
        templateStory,
      },
      { target: createTarget },
    );
  }, [dispatch, openFunnel, multiSelectSlides, createTarget, dialogId]);

  return (
    <button
      type="button"
      onClick={handleAction}
      // 8px from the button before it: the row's own gap is 2px, for the
      // icon buttons.
      className="tw-ml-1.5 tw-flex tw-h-9 tw-cursor-pointer tw-items-center tw-justify-center tw-gap-1.5 tw-whitespace-nowrap tw-rounded-lg tw-border-none tw-bg-primary tw-px-3.5 tw-font-sans tw-text-[13px] tw-font-medium tw-text-white tw-shadow-2xs hover:tw-bg-violet-500"
    >
      <IconSparkles
        className="tw-size-4 tw-shrink-0 !tw-text-white"
        stroke={1.5}
      />
      Create with AI
    </button>
  );
});
